import { useCallback, useEffect, useMemo, useState } from 'react'

import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import 'react-loading-skeleton/dist/skeleton.css'

import { IconStatsClose } from 'assets/svg/sprite'
import { Loader } from 'components/Loader/Loader'
import { usePreviewContext } from 'context/previewContext'
import PreviewComments from 'features/Preview/PreviewComments/PreviewComments'
import SinglePostPreview from 'features/Preview/SinglePostPreview/SinglePostPreview'
import { getPaginatedPost, useFilterQuery } from 'helpers/hooks'
import { getComment } from 'services/endpoints/post'
import { apiViewPost } from 'services/endpoints/tracking'
import { useUserProfile } from 'store'
import { type IComment, type IPost } from 'types/interfaces/ITypes'

import styles from './Post.module.scss'

export const Post = () => {
  const { id } = useParams<{ id: string }>()
  const postId = id ?? ''

  const navigate = useNavigate()
  const searchParams = useFilterQuery()
  const userData = useUserProfile()

  const [singleComment, setSingleComment] = useState<IComment>()
  const [hasControls, setHasControls] = useState(true)

  const {
    setInitialData,
    selectedPostData,
    setSelectedPostData,
    setCommentsActive,
    commentsActive,
    resetPreviewState,
  } = usePreviewContext()

  const removeSearchQuery = useCallback(() => {
    navigate({
      search: undefined,
    })
  }, [navigate])

  const { data, isFetching } = useQuery(
    ['paginatedSinglePost', postId],
    () => getPaginatedPost(postId),
    {
      enabled: postId.length > 0,
      refetchOnWindowFocus: false,
    },
  )

  const postData = useMemo<IPost | undefined>(
    () => data?.pages?.[0]?.page?.data?.data?.[0],
    [data],
  )

  const isSoftDeleted = useMemo(
    () =>
      Boolean((postData as any)?.deleted) ||
      Boolean((postData as any)?.deleted_at),
    [postData],
  )

  useEffect(() => {
    if (
      !postData?.id ||
      !postData?.user_id ||
      !userData?.id ||
      isSoftDeleted
    ) {
      return
    }

    const isOwnPost =
      String(postData.user_id) === String(userData.id)

    if (!isOwnPost) {
      apiViewPost(postData.id)
    }
  }, [
    postData?.id,
    postData?.user_id,
    userData?.id,
    isSoftDeleted,
  ])

  useEffect(() => {
    const commentId = searchParams.get('commentId')

    if (!commentId) {
      setSingleComment(undefined)
      return
    }

    getComment(commentId).then(response => {
      setSingleComment(response.data)
      setCommentsActive(true)
    })
  }, [searchParams, setCommentsActive])

  useEffect(() => {
    if (
      !userData?.id ||
      !data ||
      !postData ||
      isFetching
    ) {
      return
    }

    setInitialData(
      userData.id,
      'all',
      '',
      undefined,
      undefined,
      {
        selectedPost: postId,
        selectedSlide: 0,
        query: ['paginatedSinglePost', postId],
      },
    )

    setSelectedPostData(postData)
  }, [
    userData?.id,
    data,
    isFetching,
    postId,
    postData,
    setInitialData,
    setSelectedPostData,
  ])

  if (isFetching || !selectedPostData) {
    return <Loader />
  }

  return (
    <div className={styles.postContainer}>
      <div
        className={styles.close}
        onClick={() => {
          resetPreviewState()
          navigate(-1)
        }}
      >
        <IconStatsClose />
      </div>

      <SinglePostPreview
        post={selectedPostData}
        showControls={hasControls}
        isFirstPost={false}
        hasNextPage={false}
        isInViewport
        minimizeFn={() => {}}
        toggleControls={() => {
          setHasControls(current => !current)
        }}
      />

      {commentsActive &&
        (singleComment ? (
          <PreviewComments
            commentsActive={commentsActive}
            setCommentsActive={setCommentsActive}
            activeCommentsPostId={postId}
            commentCount={selectedPostData.comment_count}
            isMyPost={
              String(userData?.id) ===
              String(selectedPostData.user?.id)
            }
            isSingleCommentLink
            viewAllCommentsFn={removeSearchQuery}
          />
        ) : (
          <PreviewComments
            commentsActive={commentsActive}
            setCommentsActive={setCommentsActive}
            activeCommentsPostId={postId}
            commentCount={selectedPostData.comment_count}
            isMyPost={
              String(userData?.id) ===
              String(selectedPostData.user?.id)
            }
          />
        ))}
    </div>
  )
}